import { IconWrapper, Link } from "@caprikorn/dynamo-ui";
import { useIsFetching } from "@tanstack/react-query";
import type { FC } from "react";
import { Link as RouterLink, generatePath } from "react-router-dom";

import { InfosCard } from "@components/InfosCard/InfosCard";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { Bookmark, Phone } from "@icons";
import { ROUTES } from "@router/router.constants";
import { QUERY_KEYS } from "@services/keys";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./OverviewContainer.module.css";
import type { OverviewContainerProviderProps } from "./OverviewContainer.types";
import { OverviewConsumption } from "../OverviewConsumption/OverviewConsumption";
import { OverviewGroupCard } from "../OverviewGroupCard/OverviewGroupCard";

export const OverviewContainer: FC<OverviewContainerProviderProps> = ({
  perimeterNavLevelName,
  contactData,
  levelData,
  levelAction,
  elec,
  gas,
  areEnergiesLoading = false,
  shieldData,
  isGroupLoading = false,
}) => {
  const { currentGroup } = useGroupReferences();
  const { t } = useCustomTranslation("main");
  const overviewKeyCheckFetching = [QUERY_KEYS.CONTACT, currentGroup];
  const overviewLoading = useIsFetching({
    predicate: (query) =>
      overviewKeyCheckFetching.every((elem) => query?.queryKey.includes(elem)),
  });
  return (
    <div className={classes.container}>
      <OverviewGroupCard />
      <div className={classes.row}>
        {perimeterNavLevelName && (
          <InfosCard
            action={levelAction}
            data={levelData?.data}
            icon={
              <IconWrapper
                className={classes.icon}
                role="presentation"
                size="md"
                src={Bookmark}
              />
            }
            loading={isGroupLoading || overviewLoading !== 0}
            title={levelData?.title}
          >
            <h3 className="font-title-lg">{perimeterNavLevelName}</h3>
            {shieldData && (
              <Link
                as={RouterLink}
                to={{
                  pathname: generatePath(ROUTES.SHIELD, {
                    customerId: shieldData.customerId,
                  }),
                  search: `?siren=${shieldData?.siren}&company=${shieldData?.companyName}`,
                }}
              >
                {t("perimeter.overview.tariffShield")}
              </Link>
            )}
          </InfosCard>
        )}
        <OverviewConsumption
          elec={elec}
          gas={gas}
          isLoading={areEnergiesLoading}
        />
        <InfosCard
          key={contactData?.title}
          data={contactData?.data}
          icon={
            <IconWrapper
              className={classes.icon}
              role="presentation"
              size="md"
              src={Phone}
            />
          }
          loading={overviewLoading !== 0}
          title={contactData?.title}
        />
      </div>
    </div>
  );
};
